import React, { useEffect, useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  ButtonBase,
  Box,
  TextField,
  Button,
  Menu,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import MenuItem from '@mui/material/MenuItem';
import { convertCurrency } from '@functions/convertNumber';
import theme from '@styles/theme';
import { ChevronLeft, Delete } from '@mui/icons-material';
import { useGeneralContext } from '@context/GeneralContext';
import { useTranslation } from 'react-i18next';
import CartItem from './CartItem';
import useStyles from './checkout.styles';
import { useLambdaFunctionContext } from '@context/LambdaFunctionContext';
import { ObjectId } from 'bson';
import { useCartContext } from '@context/CartContext';
import { sendPostMessageToParent } from '@functions/trafficController';
import URLS from '@/URLS';
import { useHistory } from 'react-router-dom';
import { useStoreAppColor } from '@state/useStoreAppColor';
import { useStoreSiccMember } from '@state/useStoreSiccMember';
import { useStoreSettings } from '@state/useStoreSettings';
import { useStoreOutlet } from '@state/useStoreOutlet';
import { useStoreFirstCredential } from '@state/useStoreFirstCredential';
import { useStoreOrderCategory } from '@state/useStoreOrderCategory';
import { useStoreCartItems } from '@state/useStoreCartItems';
import { useStoreViewBill } from '@state/useStoreViewBill';
import { useCheckoutState } from '@state/checkout/useCheckoutState';
import { useShallow } from 'zustand/react/shallow';
import usePrepareBill from '@hooks/checkout/usePrepareBill';
import { getTimepicker } from '@libs/Checkout/getTimePicker';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { styled } from '@mui/system';
import useCheckOpenCloseStore from '@hooks/useCheckOpenCloseStore';
import { getTableNames } from '@libs/Checkout/getTableNames';
import { useStoreSellTime } from '@state/useStoreSellTime';
import moment from 'moment-timezone';
import { getTimezoneName } from '@libs/getTimeZone';
import { useMiniappReq } from '@state/useMiniappReq';
import { useMiniappConfirm } from '@state/useMiniappConfirm';
import useRxdbStoreLog from '@hooks/useRxdbStoreLog';
import localStorageNames from '@data/localStorageNames';
import useRxdbMiniappReq from '@hooks/useRxdbMiniappReq';
import { insertMiniappStoreLogUtil } from '@hooks/useInsertMiniappStoreLog';
import useHITApi from '@hooks/useHITApi';
import { sendErrorData } from '@functions/ErrorMessagesSender';

const CssTextField = withStyles({
  root: {
    '& label.Mui-focused': {
      color: theme.palette.secondary.main,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: theme.palette.secondary.main,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        overflow: 'none',
        border: 'none', // Disable all borders
        borderBottom: '2px solid gray', // Gray bottom border by default
        borderRadius: '0',
      },
      '&:hover fieldset': {
        borderBottom: '2px solid gray', // Keep the bottom border gray on hover
        borderRadius: '0',
      },
      '&.Mui-focused fieldset': {
        borderBottom: '2px solid black', // Black bottom border when focused
        borderRadius: '0',
      },
    },
    '&:disabled': {
      backgroundColor: 'gray', // Gray background when disabled
      color: 'black', // Black text when disabled
      cursor: 'not-allowed', // Show a "not-allowed" cursor
    },
  },
})(TextField);

const ModalOverlay = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'fixed', // Use fixed positioning
  top: '50%', // Center vertically
  left: '50%', // Center horizontally
  transform: 'translate(-50%, -50%)', // Center both axes
  width: '100%',
  maxWidth: 450,
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 1300,
}));

const ModalContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  height: '100%',
  width: '100%',
  maxWidth: '100vw',
  maxHeight: '100vh',
  overflowY: 'auto',
}));

const CheckoutPage = ({ onViewBill }) => {
  // const { setCheckoutPage, setContextState } = useCartContext()
  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const { outlet } = useStoreOutlet((state) => ({
    outlet: state.outlet,
  }));

  const classes = useStyles(appColor);
  const { updateTableName } = useCheckoutState(
    useShallow((state) => ({
      updateTableName: state.updateTableName,
    })),
  );
  const { removeSelectTime } = useCheckoutState(
    useShallow((state) => ({
      removeSelectTime: state.removeSelectTime,
    })),
  );
  const { resetCheckOut } = useCheckoutState((state) => ({
    resetCheckOut: state.resetCheckOut,
  }));

  const { prepareBillData } = usePrepareBill();

  useEffect(() => {
    removeSelectTime();
    resetCheckOut();
  }, []);

  useEffect(() => {
    updateTableName(outlet.tableName);
  }, [outlet]);

  return (
    <ModalOverlay>
      <ModalContainer>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          className={classes.modalTitle}
        // Add custom classes if needed
        >
          <CheckoutTitle />
        </Box>
        <Box padding={`${theme.spacing(2)}px 0`}>
          <CheckoutContent onViewBill={onViewBill} prepareBillData={prepareBillData} />
        </Box>
      </ModalContainer>
    </ModalOverlay>
  );
};

const CheckoutClearButton = () => {
  const { getContextState, setContextState } = useCartContext();
  const paymentResId = getContextState('paymentResId');
  const { t } = useTranslation();
  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const { removeIsViewBill } = useStoreViewBill((state) => ({
    removeIsViewBill: state.removeIsViewBill,
  }));
  const { clearCart } = useStoreCartItems((state) => ({
    clearCart: state.clearCart,
  }));
  const { removeSelectTime } = useCheckoutState(
    useShallow((state) => ({
      removeSelectTime: state.removeSelectTime,
    })),
  );
  const classes = useStyles(appColor);

  const handleClearCart = () => {
    removeSelectTime();
    removeIsViewBill();
    // localStorage.removeItem(localStorageNames.IS_VIEW_BILL);
    clearCart();
    setContextState({
      paymentResId: {
        visible: false,
      },
    });
  };

  return !paymentResId.visible ? (
    <ButtonBase
      id={'clearButton'}
      onClick={() => {
        handleClearCart();
      }}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 2,
      }}>
      <Delete sx={{ fontSize: 18 }} />
      <Typography sx={{ textAlign: 'center', fontSize: 8 }}>
        {t('voidAll')}
      </Typography>
    </ButtonBase>
  ) : (
    <ButtonBase
      className={`${classes.iconButton} ${classes.removeButton}`}></ButtonBase>
  );
};

const CheckoutTitle = () => {
  const { setCheckoutPage, setContextState } = useCartContext();
  const { t } = useTranslation();
  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const { removeSelectTime, setExpandModifier } = useCheckoutState(
    useShallow((state) => ({
      removeSelectTime: state.removeSelectTime,
      setExpandModifier: state.setExpandModifier,
    })),
  );

  const closeModal = () => {
    removeSelectTime();
    setExpandModifier(false);
    setCheckoutPage(false);
    setContextState({
      paymentResId: {
        visible: false,
      },
    });
  };

  return (
    <Box
      id={'titlePage'}
      sx={{
        backgroundColor: appColor.primaryThemeColor,
        color: appColor.secondaryFontColor,
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        height: '100%',
        alignItems: 'center',
      }}>
      <ButtonBase id={'backButton'} onClick={closeModal} sx={{ padding: 0 }}>
        <ChevronLeft sx={{ fontSize: 32, marginLeft: 2, padding: 0 }} />
      </ButtonBase>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {t('orderList').toUpperCase()}
      </Typography>
      <CheckoutClearButton />
    </Box>
  );
};

const CheckoutTaxesGrandTotal = ({ billGrandTotal }) => {
  const { getContextState } = useCartContext();
  const paymentResId = getContextState('paymentResId');
  const { visible } = paymentResId;
  const { billTaxes, billFooterNotes } = useCheckoutState(
    useShallow((state) => ({
      billTaxes: state.billTaxes,
      billFooterNotes: state.billFooterNotes,
    })),
  );

  const { getOutletId } = useStoreOutlet((state) => ({
    getOutletId: state.getOutletId,
  }));

  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const classes = useStyles(appColor);

  return (
    visible && (
      <>
        {billTaxes.map(
          (item, index) =>
            item.value !== 0 && (
              <Grid
                key={getOutletId() + index}
                container
                direction="row"
                className={classes.footer}
                spacing={0}
                sx={{ margin: 0, padding: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {item.title}
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  <b>{convertCurrency(item.value)}</b>
                </Typography>
              </Grid>
            ),
        )}
        {billGrandTotal !== 0 && (
          <>
            <hr sx={{ borderTop: '2px dashed black' }} />
            <Grid
              container
              direction="row"
              className={`${classes.borderingGrid} ${classes.footer}`}
              spacing={0}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                TOTAL
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                <b>{convertCurrency(billGrandTotal)}</b>
              </Typography>
            </Grid>
          </>
        )}
        {/* Bill footnotes under the total, e.g. "* Not subject to GST" (PLU Surcharge) */}
        {Array.isArray(billFooterNotes) &&
          billFooterNotes.map((note, index) => (
            <Grid item key={index} sx={{ textAlign: 'center', marginTop: 1 }}>
              <Typography variant="body2">{note}</Typography>
            </Grid>
          ))}
      </>
    )
  );
};

const CheckoutTablesNames = () => {
  const { getContextState } = useCartContext();
  const paymentResId = getContextState('paymentResId');
  const { t } = useTranslation();
  const { settings } = useStoreSettings((state) => ({
    settings: state.settings,
  }));
  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const classes = useStyles(appColor);
  const { siccMember } = useStoreSiccMember((state) => ({
    siccMember: state.siccMember,
  }));

  const {
    parameter,
    updateParameter,
    isErrorTextField,
    tableName,
    updateIsErrorTextField,
  } = useCheckoutState(
    useShallow((state) => ({
      parameter: state.parameter,
      updateParameter: state.updateParameter,
      isErrorTextField: state.isErrorTextField,
      tableName: state.tableName,
      updateIsErrorTextField: state.updateIsErrorTextField,
    })),
  );

  const handleInput = (event) => {
    updateIsErrorTextField(false);
    // Filter hanya alfabet dan angka, kemudian paksa menjadi huruf besar
    let filteredValue = event.target.value.replace(/[^a-zA-Z0-9\s]/g, '');
    let parameter = filteredValue.toUpperCase();
    updateParameter(parameter);
  };

  return (
    <Grid id={'parameter'} className={`${classes.typeSales} `}>
      {settings || settings.customTable === 'custom' ? (
        <>
          {settings.isQRStoreParameter || settings.customTable === 'custom' ? (
            <CssTextField
              id="standard-basic"
              borderColor="transparent"
              placeholder={
                settings.placeholderParameter
                  ? settings.placeholderParameter
                  : t('placeholderParameter')
              }
              inputProps={{
                maxLength: settings.orderMethod !== 'quick_order' ? 10 : 20,
                pattern: "[a-zA-Z0-9\\s]*",
              }}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '');
              }}
              sx={{
                minWidth: '60%',
                min: 0,
                '& .MuiInputBase-root': {
                  height: 35,
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .MuiInputBase-input': {
                  textAlign: 'center', // Center the placeholder text
                  color: paymentResId.visible ? 'black  !important' : 'inherit',
                },
                '& .MuiFormHelperText-root': {
                  textAlign: 'left',
                  width: '100%',
                  margin: 0,
                },
                '& .Mui-disabled': {
                  color: 'rgba(0, 0, 0, 1)', // Ensure black color when disabled
                  '-webkit-text-fill-color': 'rgba(0, 0, 0, 1)',
                },
              }}
              helperText={isErrorTextField ? t('helperParameter') : ''}
              error={isErrorTextField}
              value={paymentResId.visible ? tableName : parameter}
              onChange={(event) => handleInput(event)}
              autoComplete="off"
              disabled={paymentResId.visible}
            />
          ) : (
            <>
              {tableName ? (
                <Typography
                  component="span"
                  variant="body1"
                  sx={{
                    backgroundColor: appColor.backgroundColor,
                    display: 'inline-block',
                    minWidth: '60%',
                    margin: '.5rem',
                    padding: '.3rem',
                    boxShadow:
                      '0 1px 3px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                    '& .MuiSelect-select.MuiSelect-select': {
                      paddingRight: '0',
                    },
                  }}
                  className={classes.typeSalesTitle}>
                  {
                    //siccMember, settings, tableName, t
                    (settings.customTableCaption || t('table')).toUpperCase() +
                    ' : ' +
                    getTableNames(siccMember, settings, tableName)
                  }
                </Typography>
              ) : (
                siccMember && (
                  <Typography
                    component="span"
                    variant="body1"
                    sx={{
                      backgroundColor: appColor.backgroundColor,
                      display: 'inline-block',
                      minWidth: '60%',
                      margin: '.5rem',
                      padding: '.3rem',
                      boxShadow:
                        '0 1px 3px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                      '& .MuiSelect-select.MuiSelect-select': {
                        paddingRight: '0',
                      },
                    }}
                    className={classes.typeSalesTitle}>
                    {`TA-${siccMember.substring(0, siccMember.length - 2)}`}
                  </Typography>
                )
              )}
            </>
          )}
        </>
      ) : (
        <>
          {tableName ? (
            <Typography
              component="span"
              variant="body1"
              sx={{
                backgroundColor: appColor.backgroundColor,
                display: 'inline-block',
                minWidth: '60%',
                margin: '.5rem',
                padding: '.3rem',
                boxShadow:
                  '0 1px 3px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                '& .MuiSelect-select.MuiSelect-select': {
                  paddingRight: '0',
                },
              }}
              className={classes.typeSalesTitle}>
              {(settings.customTableCaption || t('table')).toUpperCase() +
                ' : ' +
                getTableNames(siccMember, settings, tableName)}
            </Typography>
          ) : (
            <Typography
              component="span"
              variant="body1"
              sx={{
                backgroundColor: appColor.backgroundColor,
                display: 'inline-block',
                minWidth: '60%',
                margin: '.5rem',
                padding: '.3rem',
                boxShadow:
                  '0 1px 3px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                '& .MuiSelect-select.MuiSelect-select': {
                  paddingRight: '0',
                },
              }}
              className={classes.typeSalesTitle}>
              {`TA-${siccMember.substring(0, siccMember.length - 2)}`}
            </Typography>
          )}
        </>
      )}
    </Grid>
  );
};

const CheckoutContent = ({ onViewBill, prepareBillData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [stateTrans, setStateTrans] = useState('');
  const [, checkOnlineStatus] = useOnlineStatus();
  const { openErrorSnackBar, showLostConnectionModal, showSellTimeAlert, showLoading } = useGeneralContext();
  const { t } = useTranslation();
  const { settings, getSetting } = useStoreSettings((state) => ({
    settings: state.settings,
    getSetting: state.getSetting,

  }));
  const { appColor } = useStoreAppColor((state) => ({
    appColor: state.appColor,
  }));
  const classes = useStyles(appColor);

  const { orderCategory, getCurrentSalesType } = useStoreOrderCategory(
    (state) => ({
      orderCategory: state.orderCategory,
      getCurrentSalesType: state.getCurrentSalesType,
    }),
  );
  const {
    setConfirmPaymentAdyen,
    setConfirmPaymentXendit,
    createXenditPaymentLink,
    openSnapPaymentTab,
    getContextState,
    setContextState
  } = useCartContext();

  const { updateSellTimeProducts, setInvalidTimeProducts } = useStoreSellTime(
    (state) => ({
      updateSellTimeProducts: state.updateSellTimeProducts,
      setInvalidTimeProducts: state.setInvalidTimeProducts,
    }),
  );

  const {
    totalQty,
    totalPrice,
    totalPriceWithCharge,
    cartItems,
    setCartItems,
    removeItemInCart,
    removePromoItem,
  } = useStoreCartItems((state) => ({
    totalQty: state.totalQty,
    totalPrice: state.totalPrice,
    totalPriceWithCharge: state.totalPriceWithCharge,
    cartItems: state.cartItems,
    setCartItems: state.setCartItems,
    removeItemInCart: state.removeItemInCart,
    removePromoItem: state.removePromoItem,
  }));

  const { siccMember } = useStoreSiccMember((state) => ({
    siccMember: state.siccMember,
  }));

  const paymentResId = getContextState('paymentResId');
  const history = useHistory();
  const { checkTokenIsExpired } = useStoreFirstCredential();
  const { getSessionAdyen } = useLambdaFunctionContext();
  const { getTableId, getOutletId, getOutletInfo } = useStoreOutlet((state) => ({
    getTableId: state.getTableId,
    getOutletId: state.getOutletId,
    getOutletInfo: state.getOutletInfo,
  }));

  const {
    serviceCharge,
    billGrandTotal,
    parameter,
    amountCurrency,
    updateIsErrorTextField,
    selectTime,
    updateSelectTime,
    setExpandModifier,
    removeParameter
  } = useCheckoutState(
    useShallow((state) => ({
      billGrandTotal: state.billGrandTotal,
      serviceCharge: state.serviceCharge,
      parameter: state.parameter,
      amountCurrency: state.amountCurrency,
      updateIsErrorTextField: state.updateIsErrorTextField,
      selectTime: state.selectTime,
      updateSelectTime: state.updateSelectTime,
      setExpandModifier: state.setExpandModifier,
      removeParameter: state.removeParameter,
    })),
  );

  const [, checkOpenCLose] = useCheckOpenCloseStore();

  const [anchorEl, setAnchorEl] = useState(null);

  const { miniappreq, getMiniappReq, updateMiniappReq, updateMiniappReqId, getMiniappReqMessage, updateMiniappReqMessage } = useMiniappReq((state) => ({
    miniappreq: state.miniappreq,
    getMiniappReq: state.getMiniappReq,
    updateMiniappReq: state.updateMiniappReq,
    updateMiniappReqId: state.updateMiniappReqId,
    getMiniappReqMessage: state.getMiniappReqMessage,
    updateMiniappReqMessage: state.updateMiniappReqMessage,
  }));

  const { getMiniappConfirm, updateMiniappConfirm, updateMiniappConfirmId } = useMiniappConfirm((state) => ({
    getMiniappConfirm: state.getMiniappConfirm,
    updateMiniappConfirm: state.updateMiniappConfirm,
    updateMiniappConfirmId: state.updateMiniappConfirmId
  }));

  const [, subscribeConfirm] = useRxdbStoreLog();
  const [ subscribeMiniappReq,,, subscribeMiniappRequestSnap] = useRxdbMiniappReq();
  const {
    updateViewBillId,
  } = useStoreViewBill((state) => ({
    updateViewBillId: state.updateViewBillId,
  }));

  const hitAPI = useHITApi();

  const handleModalWeather = (orderMethod, parameter) => {
    setContextState({
      weatherWarning: {
        visible: true,
      },
      confirmModal: {
        orderMethod: orderMethod,
        parameter: parameter,
      },
    });
  };

  const isButtonDisabled = paymentResId.visible && billGrandTotal === 0;

  const handleChangeTime = (data) => {
    updateSelectTime(data);
    setAnchorEl(null);
  };

  const setConfirmModalVisibility = (isVisible, orderMethod, parameter) => {
    setContextState({
      confirmModal: {
        visible: isVisible,
        orderMethod: orderMethod,
        parameter: parameter,
      },
    });
  };

  const handleConfirmation = (orderMethod, parameter) => {
    if (checkTokenIsExpired()) {
      sendPostMessageToParent('path', URLS.SESSION_EXPIRED);
      history.replace(URLS.SESSION_EXPIRED);
    } else {
      setConfirmModalVisibility(true, orderMethod, parameter);
    }
  };

  const confirmQuickOrder = (orderMethod) => {
    // Untuk quick_order, parameter wajib diisi (akan menjadi QR code value)
    if (!parameter || !parameter.trim()) {
      updateIsErrorTextField(true);
      openErrorSnackBar(t('helperParameter') || 'Parameter is required');
      return;
    }
    handleConfirmation(orderMethod, parameter.trim());
  };

  function removeItemsWithZeroQuantity(items) {
    return items.filter((item) => item.quantity !== 0);
  }

  const processOrderConfirmation = async () => {
    // Step 1: Remove items with zero quantity.
    const filteredCartItems = removeItemsWithZeroQuantity(cartItems);

    const timezoneName = getTimezoneName(settings.timezone); //convert +8 to Asia/Jakarta
    const currentMoment = moment.tz(timezoneName); // Get current time in specified timezone

    // Step 2: Compute invalidItems based on the current filtered cart.
    const invalidItems = filteredCartItems.filter((filteredItem) => {
      // Parse the sell_end_at time using the formatted offset.
      // Check if the current time is after the sell time.
      let sellEndMoment;
      let endAtMoment;
      if (filteredItem.is_variant && filteredItem.variant_item.length > 0) {
        // Check if variant_item exists
        filteredItem.variant_item.forEach((variant) => {
          // Iterate through variant items
          if (
            (variant.sell_end_at !== undefined ||
              variant.end_at !== undefined) &&
            variant.checked
          ) {
            if (variant.sell_end_at) {
              sellEndMoment = moment(
                variant.sell_end_at,
                'HH:mm:ss',
              );
            }
            if (variant.end_at) {
              endAtMoment = moment.tz(
                variant.end_at,
                'YYYY-MM-DD HH:mm:ss',
                timezoneName,
              );
            }
          }
        });
      } else {
        if (filteredItem.sell_end_at) {
          sellEndMoment = moment(
            filteredItem.sell_end_at,
            'HH:mm:ss',
          );
        }
        if (filteredItem.end_at) {
          endAtMoment = moment.tz(
            filteredItem.end_at,
            'YYYY-MM-DD HH:mm:ss',
            timezoneName,
          );
        }
      }

      return (
        moment().isAfter(sellEndMoment) ||
        currentMoment.isAfter(endAtMoment)
      );
    });
    
    // Now update the sellTimeProducts array:
    // Update the sellTime products based on the invalid
    updateSellTimeProducts(invalidItems);
    // Set The Invalid Products
    // For Show what products are invalids
    setInvalidTimeProducts(invalidItems);

    // Step 3: If there are invalid items, trigger the modal alert.
    if (invalidItems.length > 0) {
      showSellTimeAlert( async () => {
        // Remove invalid items from the cart.
        const validItems = filteredCartItems.filter((filteredItem) => {
          let isExpired = false;

          if (filteredItem.is_variant && filteredItem.variant_item.length > 0) {
            // For variant items, check each variant
            for (const variant of filteredItem.variant_item) {
              let sellEndMoment, endAtMoment;

              if (variant.sell_end_at || variant.end_at) {
                sellEndMoment = moment(
                  variant.sell_end_at,
                  'HH:mm:ss',
                );
                endAtMoment = moment.tz(
                  variant.end_at,
                  'YYYY-MM-DD HH:mm:ss',
                  timezoneName,
                );
                if (
                  moment().isAfter(sellEndMoment) ||
                  currentMoment.isAfter(endAtMoment)
                ) {
                  isExpired = true;
                  break; // No need to check further if one variant is expired.
                }
              }
            }
          } else {
            let sellEndMoment, endAtMoment;

            if (filteredItem.sell_end_at) {
              sellEndMoment = moment(
                filteredItem.sell_end_at,
                'HH:mm:ss',
              );
              
              if (moment().isAfter(sellEndMoment)) {
                isExpired = true;
              }
            }
            if (filteredItem.end_at) {
              endAtMoment = moment.tz(
                filteredItem.end_at,
                'YYYY-MM-DD HH:mm:ss',
                timezoneName,
              );

              if (currentMoment.isAfter(endAtMoment)) {
                isExpired = true;
              }
            }
          }

          if (isExpired) {
            removeItemInCart(filteredItem); // Optionally remove the expired item.
            return false; // Exclude this item from the validItems array.
          }

          //  remove orphan child items (child items without a valid parent).
          if (filteredItem.parentGroupId) {
            // Check if the parent Item exist.
            const parentExists = filteredCartItems.some(
              (parent) =>
                !parent.groupId &&
                parent.groupId === filteredItem.parentGroupId,
            );

            if (!parentExists) {
              // Optionally, remove the orphan child from the cart.
              removeItemInCart(filteredItem);
              return false;
            }
          }

          return true; // Include valid item.
        });

        // Second pass: remove any promo that doesnt have parent item
        const finalValidItems = validItems.filter((item) => {
          // If the item is a promo child (it has a 'promoFrom' reference)
          if (item.promoFrom) {
            // Check that there is a valid parent whose identifier matches the child's promoFrom.
            const parentExists = validItems.some(
              (parent) =>
                // A valid parent should not have a promoFrom (i.e. it isn’t itself a child)
                !parent.promoFrom &&
                // And its unique identifier (e.g. timestamp) matches the child’s promoFrom
                parent.timestamp === item.promoFrom,
            );
            if (!parentExists) {
              // If no valid parent exists, trigger removal and filter this item out.
              removePromoItem(item);
              return false;
            }
          }
          // Keep non-promo items or promo items with a valid parent.
          return true;
        });

        if (paymentResId.visible) {
          if (getOutletInfo().currency === 'idr') {
            const gateway = getSetting().activePaymentGateway ?? 'xendit';
            if (gateway === 'snap') {
              // Paid in the container's SNAP tab; the RxDB watch is the fallback signal.
              openSnapPaymentTab();
              subscribeMiniappRequestSnap();
            } else {
              const xenditResponse = await createXenditPaymentLink();
              setConfirmPaymentXendit(true, xenditResponse.data);
            }
            setIsProcessing(false);
          } else {
            // Use Adyen for other currencies
            confirmAdyenPayment();
          }
        } else {
          continueOrderConfirmation(finalValidItems);
        }
        // Continue with the rest of the order confirmation process.
      });

      // Stop the process until the user makes a decision in the modal.
      return;
    }
    if (paymentResId.visible) {
      if (getOutletInfo().currency === 'idr') {
        const gateway = getSetting().activePaymentGateway ?? 'xendit';
        if (gateway === 'snap') {
          // Paid in the container's SNAP tab; the RxDB watch is the fallback signal.
          openSnapPaymentTab();
          subscribeMiniappRequestSnap();
        } else {
          const xenditResponse = await createXenditPaymentLink();
          setConfirmPaymentXendit(true, xenditResponse.data);
        }
        setIsProcessing(false);
      } else {
        // Use Adyen for other currencies
        confirmAdyenPayment();
      }
    } else {
      continueOrderConfirmation(filteredCartItems);
    }
    // If no invalid items are found, continue normally.
  };

  const continueOrderConfirmation = async (validItems) => {
    setIsProcessing(false);
    // Update the cart items.
    // Prevent SellTime Get Update with empty array when setCartItems run again so set the updateSellTime to true
    setCartItems(validItems);
    if (settings.customTable === 'custom' && !parameter.trim()) {
      updateIsErrorTextField(true);
      return;
    }

    if (
      !getTableId() &&
      selectTime === '' &&
      settings.orderMethod !== 'quick_order' &&
      siccMember
    ) {
      openErrorSnackBar('Select Pickup Time');
      return;
    }

    if (settings && settings.orderMethod) {
      switch (settings.orderMethod) {
        case 'normal_order':
        case 'waiter_order':
          if (validItems.length > 0) {
            handleConfirmation(settings.orderMethod, parameter.trim());
          }
          break;
        case 'payment_order':
          // Untuk currency idr minimal price 1000, selainnya minimal > 0
          const currency = getOutletInfo().currency;
          const minValidAmount = currency.toLowerCase() === 'idr' ? 100 : 0;
          // Cek jika ada serviceCharge, gunakan totalPriceWithCharge, jika tidak gunakan totalPrice
          const totalAmount = serviceCharge && serviceCharge.length > 0
            ? totalPriceWithCharge(serviceCharge)
            : totalPrice();

          if (totalAmount >= minValidAmount && validItems.length > 0) {
            //pindah check miniap req rxdb
            showLoading(true);
            const id = new ObjectId().toString();
            updateMiniappConfirmId(id);
            try {
              await insertMiniappStoreLogUtil(hitAPI, getOutletId(), id);
            } catch (error) {
              console.error('Error inserting miniapp storelog:', error);
            }
            subscribeConfirm()
          } else {
            if (currency.toLowerCase() === 'idr') {
              openErrorSnackBar('Invalid Transaction Amount, Minimum Rp 100');
            } else {
              openErrorSnackBar('Invalid Transaction Amount, Minimum $0.1');
            }
          }
          break;
        case 'quick_order':
          if (validItems.length > 0) {
            confirmQuickOrder(settings.orderMethod);
          }
          break;
        default:
          if (validItems.length > 0) {
            handleConfirmation(settings.orderMethod, null);
          }
          break;
      }
    }
  };

  const confirmAdyenPayment = async () => {
    const timestamp = +new Date();
    const params = {
      _partition: getOutletId(),
      amountValue: billGrandTotal * 100, // Multiply by 100
      amountCurrency: amountCurrency,
      transId: `${paymentResId.trans_id}_${timestamp}`,
      returnUrl: import.meta.env[`VITE_REACT_APP_DOMAIN_CONTAINER_${import.meta.env.MODE}`]
    };
    const session = await getSessionAdyen(params);
    //cek session apakah berhasil di create atau tidak
    setConfirmPaymentAdyen(true, session);
    setIsProcessing(false);
    // if (!session.error) {
    // } else {
    //   openErrorSnackBar(session.message);
    //   //muncul modal payment failed atau sejenisnya
    // }
  };

  // Reset miniapp states on mount to prevent stale data from showing errors
  useEffect(() => {
    updateMiniappConfirm(false);
    updateMiniappReq(null);
    updateMiniappReqMessage(null);
  }, []);

  const handleButtonClick = async () => {
    updateMiniappConfirm(false);
    updateMiniappReq(null);
    updateMiniappReqMessage(null);
    if (isProcessing) return;

    setIsProcessing(true);
    const store = checkOpenCLose(getCurrentSalesType().currentOpen);
    if (store) {
      if (await checkOnlineStatus()) {
        if (checkTokenIsExpired()) {
          sendPostMessageToParent('path', URLS.SESSION_EXPIRED);
          history.replace(URLS.SESSION_EXPIRED);
        } else {
          if (getSetting().weatherWarning && getSetting().useSiccMember) {
            //muncul modal
            if (settings.customTable === 'custom' && !parameter.trim()) {
              updateIsErrorTextField(true);
              return;
            }
            if (
              !getTableId() &&
              selectTime === '' &&
              settings.orderMethod !== 'quick_order'
            ) {
              openErrorSnackBar('Select Pickup Time');
              return;
            }
            handleModalWeather(settings.orderMethod, parameter.trim());
          } else {
            processOrderConfirmation();
          }
        }
      } else {
        showLostConnectionModal(true);
      }
    } else {
      history.replace(URLS.OUTLET_CLOSE);
    }
  };

  const nextTransaction = async () => {
    const id = new ObjectId().toString();
    updateMiniappReqId(id)
    const x = await onViewBill(settings.orderMethod, parameter.trim());
    const transaction = JSON.parse(x);
    if (transaction.status === 'success') {
      setStateTrans(transaction);
      subscribeMiniappReq();
    } else if (transaction.status === 'items_unavailable') {
      // CartContext closed the confirm overlay and is naming the refused products on top
      // of this page; a snackbar underneath that dialog would only add noise.
      return;
    } else {
      openErrorSnackBar(transaction.message || t('failedTax'));
      sendErrorData({
        message: `tax gagal karna response ${transaction}`,
        file: 'CheckoutPage.jsx',
        func: 'nextTransaction()',
      });
    }
  }

  useEffect(() => {
    if (getMiniappConfirm() && settings.orderMethod === 'payment_order') {
      setTimeout(() => {
        nextTransaction();
      }, 200);
    }
    // eslint-disable-next-line
  }, [getMiniappConfirm()]);


  useEffect(() => {
    // Only process if we have a valid miniapp request response (not null/undefined)
    // and we're in the confirmation flow
    if (getMiniappConfirm()  && settings.orderMethod === 'payment_order' && getMiniappReq() !== null && stateTrans) {
      if (getMiniappReq() === 'updated') {
        setTimeout(() => {
          localStorage.setItem(
            localStorageNames.MINIAPP_REQ_ID,
            stateTrans.miniapp_request_id,
          );
          setExpandModifier(true);
          setContextState({
            paymentResId: {
              trans_id: stateTrans.trans_id,
              miniapp_request_id: stateTrans.miniapp_request_id,
              visible: true,
            },
          });

          if (getOutletInfo().currency === 'idr') {
            updateViewBillId(stateTrans.trans_id);
          }
          removeParameter();

          // Call prepareBillData after setting the payment context
          if (prepareBillData && stateTrans.trans_id && stateTrans.miniapp_request_id) {
            setTimeout(() => {
              prepareBillData(stateTrans.trans_id, stateTrans.miniapp_request_id);
            }, 3000);
          }
        }, 200);
      } else {
        showLoading(false);
        openErrorSnackBar(getMiniappReqMessage() || t('failedTax'));
        sendErrorData({
          message: `tax gagal karna subscribe miniapp request ${getMiniappConfirm(), getMiniappReq()}`,
          file: 'CheckoutPage.jsx',
          func: 'useEffect()',
        });
      }
      updateMiniappConfirm(false);
    }
    // eslint-disable-next-line
  }, [miniappreq, stateTrans]);

  return (
    <Paper
      className={classes.contentContainer}
      elevation={3}
      sx={{ padding: '1rem' }}>
      <Box
        id={'bodyPage'}
        className={classes.innerContentContainer}
        key={getOutletId()}>
        <CheckoutTablesNames />

        <Grid id={'salesType'} className={`${classes.typeSales}`}>
          {!getTableId() &&
            settings.orderMethod !== 'quick_order' &&
            siccMember ? (
            <>
              <Button
                className={classes.typeSalesTitle}
                sx={{
                  color: appColor.primaryFontColor,
                  fontWeight: 'bold',
                  textTransform: 'capitalize',
                  display: 'inline-block',
                  minWidth: '60%',
                  margin: '.5rem',
                  padding: '.3rem',
                  boxShadow:
                    '0 1px 3px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                  '& .MuiSelect-select.MuiSelect-select': {
                    paddingRight: '0',
                  },
                }}
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                }}>
                {selectTime !== '' ? selectTime : `Please Choose Pickup Time`}
              </Button>
              {Boolean(anchorEl) && (
                <Menu
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  classes={{ paper: classes.inputCustom }}>
                  {getTimepicker(
                    getCurrentSalesType().currentOpen,
                    settings.timezone,
                  ).map((item) => (
                    <MenuItem
                      value={item}
                      onClick={() => {
                        handleChangeTime(item);
                      }}>
                      {item}
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </>
          ) : (
            <Typography
              component="span"
              variant="body1"
              sx={{
                backgroundColor: appColor.backgroundColor,
                display: 'inline-block',
                minWidth: '60%',
                margin: '.5rem',
                padding: '.3rem',
                boxShadow:
                  '0 1px 3px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
                '& .MuiSelect-select.MuiSelect-select': {
                  paddingRight: '0',
                },
              }}
              className={classes.typeSalesTitle}>
              {orderCategory.title}
            </Typography>
          )}
        </Grid>

        <CartItem />

        <Grid
          id={'totalQty'}
          container
          direction="row"
          className={`${classes.footer}`}
          sx={{ margin: 0, padding: 0 }}>
          <Grid item>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              TOTAL QTY
            </Typography>
          </Grid>
          <Grid item>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              <b>{totalQty()}</b>
            </Typography>
          </Grid>
        </Grid>

        <Grid
          id={'totalNominal'}
          container
          direction="row"
          className={`${classes.borderingGrid} ${classes.footer}`}
          sx={{ margin: 0, padding: 0 }}>
          <Grid item>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {settings.orderMethod === 'payment_order' ? 'SUBTOTAL' : 'TOTAL'}
            </Typography>
          </Grid>
          <Grid item>
            {serviceCharge ? (
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                <b>{convertCurrency(totalPriceWithCharge(serviceCharge))}</b>
              </Typography>
            ) : (
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                <b>{convertCurrency(totalPrice())}</b>
              </Typography>
            )}
          </Grid>
        </Grid>

        <CheckoutTaxesGrandTotal billGrandTotal={billGrandTotal} />

        <Grid item sx={{ width: '100%', textAlign: 'center' }}>
          <Button
            classes={{ label: 'class-btn-confirm' }}
            id={'confirmButton'}
            onClick={handleButtonClick}
            className={classes.buttonConfirm}
            sx={{
              width: '100%',
              color: isProcessing ? 'black' : appColor.secondaryFontColor,
              backgroundColor: isProcessing
                ? 'gray'
                : appColor.primaryThemeColor,
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              marginTop: '.5rem',
              textWeight: 'bold',
              '&:hover': {
                backgroundColor: appColor.primaryThemeColor,
              },
            }}
            variant="contained"
            disabled={isProcessing}>
            {paymentResId.visible
              ? 'Proceed to payment'
              : settings.orderMethod === 'payment_order'
                ? 'Proceed to Checkout'
                : t('confirmOrder')}
          </Button>
        </Grid>
      </Box>
    </Paper>
  );
};

export default CheckoutPage;
